/**
 * Candidate Selection Sidebar
 *
 * Lists all candidates in a configuration for selection.
 * Allows running debug processing on selected candidate.
 */

import { Badge, Box, Button, HStack, IconButton, Input, Text, VStack } from '@chakra-ui/react';
import { Play, RefreshCw, Search } from 'lucide-react';
import { type FC, useMemo, useState } from 'react';
import { CollapsibleSidebar } from '~/components/layout/page-utilities/CollapsibleSidebar';
import { LAYOUT_SPACING } from '~/components/layout/shared/spacing';
import type { Candidate } from '~/types';

interface CandidateSelectionSidebarProps {
  candidates: Candidate[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onRunProcessing: () => void;
  isProcessing: boolean;
}

export const CandidateSelectionSidebar: FC<CandidateSelectionSidebarProps> = ({
  candidates,
  selectedId,
  onSelect,
  onRunProcessing,
  isProcessing,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter candidates by search term
  const filteredCandidates = useMemo(() => {
    if (!searchTerm.trim()) return candidates;
    const term = searchTerm.toLowerCase();
    return candidates.filter((c) => c.name.toLowerCase().includes(term));
  }, [candidates, searchTerm]);

  return (
    <CollapsibleSidebar
      side="left"
      isCollapsed={isCollapsed}
      onToggle={() => setIsCollapsed(!isCollapsed)}
      expandedWidth={LAYOUT_SPACING.SIDEBAR_EXPANDED_SM}
    >
      {isCollapsed ? (
        // Collapsed state: Only show rerun button as icon
        <Box mt={4}>
          <IconButton
            aria-label="Rerun processing"
            size="sm"
            variant="outline"
            colorPalette="blue"
            disabled={!selectedId || isProcessing}
            loading={isProcessing}
            onClick={onRunProcessing}
          >
            <RefreshCw size={16} />
          </IconButton>
        </Box>
      ) : (
        // Expanded state: Full sidebar with candidate selection
        <VStack align="stretch" gap={4} p={4} h="full">
          {/* Header */}
          <VStack align="stretch" gap={2}>
            <Text fontWeight="semibold" fontSize="sm">
              Select Candidate
            </Text>
            <Text fontSize="xs" color="fg.muted">
              {candidates.length} candidates available
            </Text>
          </VStack>

          {/* Search */}
          <Box position="relative">
            <Input
              size="sm"
              placeholder="Search candidates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              pl={8}
            />
            <Search
              size={14}
              style={{
                position: 'absolute',
                left: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                opacity: 0.5,
              }}
            />
          </Box>

          {/* Run Processing Button */}
          <Button
            size="sm"
            colorPalette="blue"
            disabled={!selectedId || isProcessing}
            loading={isProcessing}
            onClick={onRunProcessing}
          >
            <Play size={14} />
            Run Processing
          </Button>

          {/* Candidate List */}
          <VStack align="stretch" gap={1} flex={1} overflowY="auto">
            {filteredCandidates.length === 0 && (
              <Text fontSize="sm" color="fg.muted" textAlign="center" py={4}>
                No candidates found
              </Text>
            )}
            {filteredCandidates.map((candidate) => (
              <CandidateListItem
                key={candidate.id}
                candidate={candidate}
                isSelected={candidate.id === selectedId}
                onSelect={() => onSelect(candidate.id)}
              />
            ))}
          </VStack>
        </VStack>
      )}
    </CollapsibleSidebar>
  );
};

interface CandidateListItemProps {
  candidate: Candidate;
  isSelected: boolean;
  onSelect: () => void;
}

const CandidateListItem: FC<CandidateListItemProps> = ({ candidate, isSelected, onSelect }) => {
  const isViable = candidate.results?.isViable ?? false;
  const totalPrincipal = candidate.results?.totalPrincipal ?? 0;

  return (
    <Box
      p={2}
      borderRadius="md"
      cursor="pointer"
      bg={isSelected ? 'blue.subtle' : 'transparent'}
      borderWidth="1px"
      borderColor={isSelected ? 'blue.emphasized' : 'transparent'}
      _hover={{
        bg: isSelected ? 'blue.subtle' : 'bg.subtle',
      }}
      onClick={onSelect}
    >
      <HStack justify="space-between" mb={1}>
        <Text fontSize="sm" fontWeight={isSelected ? 'semibold' : 'normal'} lineClamp={1}>
          {candidate.name}
        </Text>
        <Badge size="sm" colorPalette={isViable ? 'green' : 'red'}>
          {isViable ? 'Viable' : 'Failed'}
        </Badge>
      </HStack>
      <Text fontSize="xs" color="fg.muted">
        ${totalPrincipal.toLocaleString()} principal
      </Text>
    </Box>
  );
};
